/**
 * Prisma Pneus — Conversion Tracking
 * Versão: 1.1.0
 * Captura cliques em WhatsApp e telefone via event delegation global
 * e dispara eventos para GA4 (via gtag), Shopify Analytics e dataLayer.
 *
 * v1.1.0: Adiciona fila de eventos resiliente a gtag carregado assíncrono.
 *         Se gtag não estiver disponível no momento do clique, evento é
 *         enfileirado e disparado quando gtag ficar disponível (verifica a cada 250ms,
 *         por até 10 segundos). Após esse timeout, eventos pendentes são descartados.
 */
(function() {
  'use strict';

  var VERSION = '1.1.0';
  var DEBOUNCE_MS = 500;
  var GTAG_WAIT_INTERVAL_MS = 250;
  var GTAG_WAIT_TIMEOUT_MS = 10000;

  var lastFired = {};
  var pendingGtagQueue = [];
  var gtagPollingStarted = false;
  var gtagPollingStartTime = null;

  function isDebug() {
    return /[?&]prisma_debug=1/.test(window.location.search)
        || window.localStorage.getItem('prisma_debug') === '1';
  }

  function log() {
    if (!isDebug()) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[prisma-tracking]');
    console.log.apply(console, args);
  }

  function detectChannel(href, dataset) {
    if (!href && !dataset) return null;
    href = href || '';
    var ds = dataset || {};

    if (ds.track === 'whatsapp') return 'whatsapp';
    if (ds.track === 'phone') return 'phone';

    if (/wa\.me|api\.whatsapp\.com|whatsapp\.com\/send/i.test(href)) {
      return 'whatsapp';
    }
    if (/^tel:/i.test(href)) {
      return 'phone';
    }
    return null;
  }

  function detectLojaFromPath() {
    var path = window.location.pathname.toLowerCase();
    var match = path.match(/loja-(lapa|tatuape|perdizes|vila-prudente)/);
    if (match) return match[1];
    return null;
  }

  function getProductContext() {
    var ctx = {};
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) {
      var meta = window.ShopifyAnalytics.meta;
      if (meta.product) {
        ctx.product_id = meta.product.id || null;
        ctx.product_name = (meta.product.variants && meta.product.variants[0] && meta.product.variants[0].name) || null;
        if (meta.product.variants && meta.product.variants[0]) {
          ctx.sku = meta.product.variants[0].sku || null;
          ctx.value = (meta.product.variants[0].price || 0) / 100;
          ctx.currency = meta.currency || 'BRL';
        }
      }
    }
    return ctx;
  }

  function shouldDebounce(channel, href) {
    var key = channel + '|' + href;
    var now = Date.now();
    if (lastFired[key] && (now - lastFired[key]) < DEBOUNCE_MS) {
      return true;
    }
    lastFired[key] = now;
    return false;
  }

  function processGtagQueue() {
    if (typeof window.gtag !== 'function') return false;
    if (pendingGtagQueue.length === 0) return true;

    log('gtag now available, flushing queue:', pendingGtagQueue.length, 'events');
    while (pendingGtagQueue.length > 0) {
      var item = pendingGtagQueue.shift();
      try {
        window.gtag('event', item.eventName, item.params);
        log('gtag fired (from queue)', item.eventName, item.params);
      } catch (e) {
        log('gtag failed (from queue)', e);
      }
    }
    return true;
  }

  function startGtagPolling() {
    if (gtagPollingStarted) return;
    gtagPollingStarted = true;
    gtagPollingStartTime = Date.now();

    var pollId = setInterval(function() {
      if (processGtagQueue()) {
        clearInterval(pollId);
        log('gtag polling complete, queue drained');
        return;
      }
      if (Date.now() - gtagPollingStartTime > GTAG_WAIT_TIMEOUT_MS) {
        clearInterval(pollId);
        log('gtag polling timeout after', GTAG_WAIT_TIMEOUT_MS, 'ms. Dropping', pendingGtagQueue.length, 'pending events');
        pendingGtagQueue = [];
      }
    }, GTAG_WAIT_INTERVAL_MS);
  }

  function sendToGtag(eventName, params) {
    if (typeof window.gtag === 'function') {
      try {
        window.gtag('event', eventName, params);
        log('gtag fired', eventName, params);
        return;
      } catch (e) {
        log('gtag failed immediately', e);
      }
    }
    log('gtag not available yet, queueing:', eventName);
    pendingGtagQueue.push({ eventName: eventName, params: params });
    startGtagPolling();
  }

  function sendToDataLayer(eventName, params) {
    window.dataLayer = window.dataLayer || [];
    var dlEvent = { event: eventName };
    Object.keys(params).forEach(function(k) { dlEvent[k] = params[k]; });
    window.dataLayer.push(dlEvent);
  }

  function sendToShopifyAnalytics(eventName, params) {
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib && typeof window.ShopifyAnalytics.lib.track === 'function') {
      try {
        window.ShopifyAnalytics.lib.track(eventName, params);
      } catch (e) {
        log('ShopifyAnalytics.track failed', e);
      }
    }
  }

  function dispatchCustomEvent(channel, params) {
    document.dispatchEvent(new CustomEvent('prisma:lead_click', {
      detail: Object.assign({ channel: channel }, params)
    }));
  }

  function fireEvent(channel, el) {
    var href = el.getAttribute('href') || '';
    var ds = el.dataset || {};

    if (shouldDebounce(channel, href)) {
      log('debounced', channel, href);
      return;
    }

    var eventName = channel === 'whatsapp' ? 'whatsapp_lead' : 'phone_lead';
    var productCtx = getProductContext();

    var params = {
      event_category: 'lead',
      event_label: ds.loja || detectLojaFromPath() || 'sem_loja',
      loja: ds.loja || detectLojaFromPath() || null,
      sku: ds.sku || productCtx.sku || null,
      product_id: ds.productId || productCtx.product_id || null,
      product_name: ds.productName || productCtx.product_name || null,
      value: parseFloat(ds.price) || productCtx.value || null,
      currency: 'BRL',
      page_path: window.location.pathname,
      page_title: document.title,
      destination_url: href
    };

    Object.keys(params).forEach(function(k) {
      if (params[k] === null || params[k] === undefined || params[k] === '') {
        delete params[k];
      }
    });

    sendToGtag(eventName, params);
    sendToDataLayer(eventName, params);
    sendToShopifyAnalytics(eventName, params);
    dispatchCustomEvent(channel, params);
  }

  function handleClick(e) {
    var el = e.target.closest ? e.target.closest('a, button') : null;
    if (!el) return;

    var href = el.getAttribute('href') || '';
    var channel = detectChannel(href, el.dataset);
    if (!channel) return;

    fireEvent(channel, el);
  }

  function init() {
    document.addEventListener('click', handleClick, true);
    log('Prisma tracking initialized', 'v' + VERSION);
    window.PRISMA_TRACKING_VERSION = VERSION;
    if (typeof window.gtag === 'function') {
      log('gtag already available at init time');
    } else {
      log('gtag not yet available at init time, will queue if needed');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
